/**
 * Shared cookie domain resolution + option builders for aks_session / aks_cart / aks_prefs.
 *
 * This is the **B2C apex resolver** — used by dashboard, landing-page, test-engine, and
 * course-engine only. `partner-portal` does NOT copy this file: `studio.aksademia.com`
 * *ends with* `.aksademia.com`, so this resolver's own rule 2 below would correctly (for
 * B2C purposes) but WRONGLY for B2B share the `.aksademia.com` apex with it, leaking a
 * partner-portal session cookie onto the shared B2C domain. partner-portal ships a
 * separate host-only-by-default resolver instead (see Task 16, spec §4).
 *
 * White-label correctness (see docs/b2b/debranding-scope.md §6): a hardcoded apex
 * (`.aksademia.com`) is rejected by the browser when the page is served from a
 * tenant's own domain, so the cookie is never stored. Resolve per-deployment instead:
 *
 *   1. `NEXT_PUBLIC_COOKIE_DOMAIN` — explicit per-deployment override. Set it to the
 *      tenant apex (e.g. `.tenant.com`) when a white-label deployment spans multiple
 *      subdomains and needs cross-subdomain SSO.
 *   2. aksademia.com hosts — share the `.aksademia.com` apex so SSO keeps working
 *      across our own subdomains (dashboard / test-engine / course-engine / landing).
 *   3. anything else (localhost, IPs, a tenant's custom domain) — host-only (omit `domain`).
 *
 * Client-safe: no node:crypto, no server-only imports — usable from both Next route
 * handlers (server) and client components (js-cookie). See
 * docs/superpowers/specs/2026-07-18-session-cookie-auth-design.md §4. Duplicated per
 * B2C app; keep the copies in sync until a shared client-safe package exists.
 */

const AKSADEMIA_APEX = '.aksademia.com';

/** httpOnly session cookie name — the single source of truth for auth. */
export const SESSION_COOKIE = 'aks_session';

export function resolveCookieDomain(hostname: string): string | undefined {
  const configured = process.env.NEXT_PUBLIC_COOKIE_DOMAIN?.trim();
  if (configured) return configured;

  if (hostname === 'aksademia.com' || hostname.endsWith(AKSADEMIA_APEX)) {
    return AKSADEMIA_APEX;
  }

  return undefined;
}

interface SessionCookieSetOptions {
  httpOnly: true;
  secure: true;
  sameSite: 'lax';
  path: '/';
  domain?: string;
  maxAge: number;
}

/** Options for setting `aks_session` on a Next `NextResponse` cookie store. */
export function sessionCookieSetOptions(host: string, maxAgeSeconds: number): SessionCookieSetOptions {
  const domain = resolveCookieDomain(host);
  return {
    httpOnly: true,
    secure: true,
    sameSite: 'lax',
    path: '/',
    maxAge: maxAgeSeconds,
    ...(domain ? { domain } : {}),
  };
}

interface SessionCookieClearOptions {
  httpOnly: true;
  secure: true;
  sameSite: 'lax';
  path: '/';
  domain?: string;
  maxAge: 0;
}

/** Options for clearing `aks_session` — domain must match how it was set, or the browser keeps it. */
export function sessionCookieClearOptions(host: string): SessionCookieClearOptions {
  const domain = resolveCookieDomain(host);
  return {
    httpOnly: true,
    secure: true,
    sameSite: 'lax',
    path: '/',
    maxAge: 0,
    ...(domain ? { domain } : {}),
  };
}

interface ClientCookieAttributes {
  expires: number;
  path: '/';
  sameSite: 'lax';
  secure: true;
  domain?: string;
}

/** Attributes for a client-readable cookie (`aks_cart`, `aks_prefs`) written via js-cookie. */
export function clientCookieAttributes(maxAgeDays: number): ClientCookieAttributes {
  const domain = resolveCookieDomain(window.location.hostname);
  return {
    expires: maxAgeDays,
    path: '/',
    sameSite: 'lax',
    secure: true,
    ...(domain ? { domain } : {}),
  };
}
